import React from "react";
import { MDBSpinner, MDBIcon, MDBBtn } from "mdb-react-ui-kit";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import SellTicketModal from "./SellTicketModal";
import "./Venues.css";

/**
 * SeatMapView — mapa visual de butacas para una función con sala asignada.
 *
 * Props:
 *  - idVenue: integer — ID de la sala
 *  - idRepresentacionFecha: integer — ID de la sesión
 *  - canSell: boolean
 *  - canManage: boolean
 *  - onRefreshBatches: () => void — callback para que BatchesModal recargue sus lotes
 */
class SeatMapView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      venue: null,
      seatStatusMap: {},   // idSeat → { status, ticket, batchName, batchPrice, idZone }
      loading: false,
      // sell modal
      sellOpen: false,
      sellTicket: null,
      sellBatchPrice: null,
    };
  }

  componentDidMount() {
    if (this.props.idVenue && this.props.idRepresentacionFecha) {
      this.load();
    }
  }

  componentDidUpdate(prevProps) {
    const { idVenue, idRepresentacionFecha } = this.props;
    if (
      prevProps.idVenue !== idVenue ||
      prevProps.idRepresentacionFecha !== idRepresentacionFecha
    ) {
      this.load();
    }
  }

  // ─── data loading ─────────────────────────────────────────────────────────

  load = () => {
    const { idVenue, idRepresentacionFecha } = this.props;
    if (!idVenue || !idRepresentacionFecha) return;
    this.setState({ loading: true, seatStatusMap: {} });

    // 1. Cargar la sala (estructura completa)
    const venuePromise = doFetchJson("TEAN", "getVenue/" + idVenue);
    // 2. Cargar los lotes de la sesión
    const batchesPromise = doFetchJson(
      "TEAN",
      "getTicketBatches?idRepresentacionFecha=" + idRepresentacionFecha
    );

    Promise.all([venuePromise, batchesPromise])
      .then(([venueJson, batchesJson]) => {
        if (!venueJson || venueJson.STATE !== "SUCCESS") {
          const msg = venueJson?.STATE === "404"
            ? "La sala asignada a esta función no existe. Revisa la configuración de la función."
            : "No se pudo cargar la sala";
          toast.show({ message: msg, type: "danger" });
          this.setState({ loading: false });
          return;
        }
        const venue = venueJson.DATA;
        const batches = (batchesJson && batchesJson.STATE === "SUCCESS" && batchesJson.DATA) || [];

        // Filtrar lotes con al menos una zona asignada (nuevo modelo muchos-a-muchos)
        const zonalBatches = batches.filter(
          (b) => Array.isArray(b.assignedZones) && b.assignedZones.length > 0
        );

        if (!zonalBatches.length) {
          this.setState({ venue, seatStatusMap: {}, loading: false });
          return;
        }

        // Mapa inverso: idZone → batch (para lookup O(1) por zona)
        const zoneIdToBatch = {};
        zonalBatches.forEach((batch) => {
          (batch.assignedZones || []).forEach((z) => {
            zoneIdToBatch[z.idZone] = batch;
          });
        });

        // 3. Cargar tickets de cada lote zonal (deduplicar por idBatch)
        const uniqueBatches = [...new Map(zonalBatches.map((b) => [b.idBatch, b])).values()];
        const ticketPromises = uniqueBatches.map((batch) =>
          doFetchJson("TEAN", "getTickets?idBatch=" + batch.idBatch).then((json) => ({
            batch,
            tickets: json && json.STATE === "SUCCESS" ? json.DATA || [] : [],
          }))
        );

        Promise.all(ticketPromises).then((results) => {
          // Indice por lote: idBatch → { ticketNumber(string) → ticket }
          const batchTickets = {};
          results.forEach(({ batch, tickets }) => {
            const byNum = {};
            tickets.forEach((t) => {
              if (t.ticketNumber != null) byNum[String(t.ticketNumber)] = t;
            });
            batchTickets[batch.idBatch] = { byNum, batch };
          });

          // Construir seatStatusMap: idSeat → { status, ticket, batchName, batchPrice, idZone }
          const seatStatusMap = {};
          (venue.zones || []).forEach((zone) => {
            const batch = zoneIdToBatch[zone.idZone];
            if (!batch) return;
            const entry = batchTickets[batch.idBatch];
            if (!entry) return;
            (zone.seats || []).forEach((seat) => {
              if (!seat.seatNumber) return;
              // seatNumber ya es el label único: "A-1", "A-2"… (BY_ROW_LABEL)
              // o el número simple "1", "2"… (otros esquemas)
              const ticket = entry.byNum[String(seat.seatNumber)];
              if (ticket) {
                seatStatusMap[seat.idSeat] = {
                  status: ticket.status,
                  ticket,
                  batchName: batch.name,
                  batchPrice: batch.price,
                  idZone: zone.idZone,
                };
              }
            });
          });
          this.setState({ venue, seatStatusMap, loading: false });
        });
      })
      .catch(() => {
        toast.show({ message: "Error de red al cargar el mapa", type: "danger" });
        this.setState({ loading: false });
      });
  };

  // ─── sell modal ───────────────────────────────────────────────────────────

  openSell = (ticket, batchPrice) => {
    this.setState({ sellOpen: true, sellTicket: ticket, sellBatchPrice: batchPrice });
  };

  closeSell = () => this.setState({ sellOpen: false });

  onTicketSold = (ticket) => {
    // Actualizar el estado del ticket localmente buscándolo por idTicket
    this.setState((prev) => {
      const updated = {};
      Object.entries(prev.seatStatusMap).forEach(([idSeat, entry]) => {
        if (entry.ticket && entry.ticket.idTicket === ticket.idTicket) {
          updated[idSeat] = { ...entry, status: "SOLD", ticket };
        } else {
          updated[idSeat] = entry;
        }
      });
      return { seatStatusMap: updated, sellOpen: false };
    });
    if (this.props.onRefreshBatches) this.props.onRefreshBatches();
  };

  // ─── render ───────────────────────────────────────────────────────────────

  cellStatusClass(idSeat, seat) {
    if (!seat || !seat.enabled) return "seat-map-cell--disabled";
    const entry = this.state.seatStatusMap[idSeat];
    if (!entry) return "seat-map-cell--noticket";
    switch (entry.status) {
      case "AVAILABLE": return "seat-map-cell--available";
      case "SOLD": return "seat-map-cell--sold";
      case "RESERVED": return "seat-map-cell--reserved";
      default: return "seat-map-cell--noticket";
    }
  }

  handleCellClick = (seat) => {
    if (!seat || !seat.enabled) return;
    const entry = this.state.seatStatusMap[seat.idSeat];
    if (!entry || entry.status !== "AVAILABLE") return;
    if (!this.props.canSell) return;
    this.openSell(entry.ticket, entry.batchPrice);
  };

  findZoneForCell = (row, col) => {
    const { venue } = this.state;
    if (!venue || !Array.isArray(venue.zones)) return null;
    return venue.zones.find(
      (z) => row >= z.rowStart && row <= z.rowEnd && col >= z.colStart && col <= z.colEnd
    ) || null;
  };

  findSeat = (row, col, zone) => {
    if (!zone || !Array.isArray(zone.seats)) return null;
    return zone.seats.find((s) => s.rowIdx === row && s.colIdx === col) || null;
  };

  hexToRgba = (hex, alpha) => {
    if (!hex || !hex.startsWith("#")) return `rgba(200,200,200,${alpha})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  renderGrid() {
    const { venue, seatStatusMap } = this.state;
    const { canSell } = this.props;
    if (!venue) return null;
    const { gridRows, gridCols } = venue;

    const rows = [];
    for (let r = 0; r < gridRows; r++) {
      const cells = [];
      for (let c = 0; c < gridCols; c++) {
        const zone = this.findZoneForCell(r, c);
        const seat = this.findSeat(r, c, zone);
        const statusClass = this.cellStatusClass(seat ? seat.idSeat : null, seat);

        let bg = "#f9fafb";
        let title = `(${r},${c})`;
        let label = null;
        let clickable = false;

        if (zone) {
          bg = this.hexToRgba(zone.color || "#3b82f6", 0.15);
        }

        if (seat) {
          const entry = seatStatusMap[seat.idSeat];
          label = seat.seatNumber;
          if (entry) {
            const statusLabel = entry.status === "SOLD" ? "Vendida" : entry.status === "RESERVED" ? "Reservada" : "Disponible";
            title = `${zone ? zone.name + " — " : ""}${seat.seatNumber || `F${r}C${c}`}\n${statusLabel}${entry.batchName ? " · " + entry.batchName : ""}`;
            if (entry.status === "AVAILABLE" && canSell) {
              clickable = true;
            }
          } else {
            title = `${zone ? zone.name + " — " : ""}${seat.seatNumber || `F${r}C${c}`}\nSin entrada asignada`;
          }
        }

        cells.push(
          <div
            key={`${r}-${c}`}
            className={`seat-map-cell ${statusClass}`}
            style={{ background: statusClass === "seat-map-cell--noticket" || statusClass === "seat-map-cell--disabled" ? bg : undefined }}
            title={title}
            onClick={clickable ? () => this.handleCellClick(seat) : undefined}
          >
            {label && <span>{label}</span>}
          </div>
        );
      }
      rows.push(
        <div key={r} className="venue-grid-row" style={{ display: "contents" }}>
          {cells}
        </div>
      );
    }

    return (
      <div
        className="seat-map-grid"
        style={{ gridTemplateColumns: `repeat(${gridCols}, 2rem)` }}
      >
        {rows}
      </div>
    );
  }

  renderStats() {
    const { seatStatusMap } = this.state;
    const entries = Object.values(seatStatusMap);
    const available = entries.filter((e) => e.status === "AVAILABLE").length;
    const sold = entries.filter((e) => e.status === "SOLD").length;
    const reserved = entries.filter((e) => e.status === "RESERVED").length;
    const total = entries.length;
    return (
      <div className="d-flex gap-3 flex-wrap mb-2 small text-muted">
        <span>
          <strong className="text-success">{available}</strong> disponibles
        </span>
        <span>
          <strong className="text-danger">{sold}</strong> vendidas
        </span>
        <span>
          <strong className="text-warning">{reserved}</strong> reservadas
        </span>
        <span>
          <strong>{total}</strong> total con entrada
        </span>
      </div>
    );
  }

  render() {
    const { loading, venue, sellOpen, sellTicket, sellBatchPrice } = this.state;
    const { canSell } = this.props;

    if (loading) {
      return (
        <div className="text-center py-4">
          <MDBSpinner />
          <div className="text-muted small mt-2">Cargando mapa de sala…</div>
        </div>
      );
    }

    if (!venue) {
      return (
        <div className="text-muted fst-italic text-center py-3">
          No hay sala asignada a esta función.
        </div>
      );
    }

    return (
      <div>
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div>
            <MDBIcon fas icon="building" className="me-2 text-secondary" />
            <span className="fw-semibold">{venue.name}</span>
            <span className="text-muted small ms-2">
              {venue.gridRows} × {venue.gridCols}
            </span>
          </div>
          <MDBBtn size="sm" color="light" onClick={this.load} title="Recargar">
            <MDBIcon fas icon="sync-alt" />
          </MDBBtn>
        </div>

        {/* Stats */}
        {this.renderStats()}

        {/* Legend */}
        <div className="seat-map-legend mb-2">
          <span className="seat-map-legend-item">
            <span className="seat-map-legend-dot" style={{ background: "#dcfce7", borderColor: "#86efac" }} />
            Disponible{canSell ? " (clic para vender)" : ""}
          </span>
          <span className="seat-map-legend-item">
            <span className="seat-map-legend-dot" style={{ background: "#fee2e2", borderColor: "#fca5a5" }} />
            Vendida
          </span>
          <span className="seat-map-legend-item">
            <span className="seat-map-legend-dot" style={{ background: "#ffedd5", borderColor: "#fdba74" }} />
            Reservada
          </span>
          <span className="seat-map-legend-item">
            <span className="seat-map-legend-dot" style={{ background: "#f9fafb", borderColor: "#e5e7eb", opacity: 0.5 }} />
            Sin entrada
          </span>
          <span className="seat-map-legend-item">
            <span className="seat-map-legend-dot" style={{ background: "#f3f4f6", borderColor: "#d1d5db", opacity: 0.4 }} />
            Inhabilitada
          </span>
        </div>

        {/* Grid */}
        <div className="seat-map-wrapper">
          {this.renderGrid()}
        </div>

        {/* Sell modal */}
        <SellTicketModal
          show={sellOpen}
          ticket={sellTicket}
          batchPrice={sellBatchPrice}
          onClose={this.closeSell}
          onSold={this.onTicketSold}
        />
      </div>
    );
  }
}

export default SeatMapView;
